import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ArrowLeft, Pencil, Trash2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { deleteProfessional, getProfessional } from "@/lib/api/professionals";
import { listVisits } from "@/lib/api/visits";
import { listPatients } from "@/lib/api/patients";
import { ProfessionalDialog } from "@/components/professional-dialog";
import { useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/profesionales/$id")({
  head: () => ({
    meta: [
      { title: "Ficha de profesional · Rafael González" },
      { name: "description", content: "Datos del profesional y próximas citas asignadas." },
    ],
  }),
  component: ProfessionalDetailPage,
});

function ProfessionalDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);

  const { data: prof, isLoading } = useQuery({ queryKey: ["professionals", id], queryFn: () => getProfessional(id) });
  const { data: visits = [] } = useQuery({ queryKey: ["visits", { professionalId: id }], queryFn: () => listVisits({ professionalId: id }) });
  const { data: patients = [] } = useQuery({ queryKey: ["patients"], queryFn: () => listPatients() });
  const patientById = Object.fromEntries(patients.map((p) => [p.id, p]));

  const deleteMutation = useMutation({
    mutationFn: () => deleteProfessional(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["professionals"] });
      toast.success("Profesional eliminado");
      navigate({ to: "/profesionales" });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (isLoading) return <div className="text-muted-foreground">Cargando...</div>;
  if (!prof) return <div className="text-muted-foreground">Profesional no encontrado</div>;

  const upcoming = visits
    .filter((v) => new Date(v.start) >= new Date() && v.status === "scheduled")
    .slice(0, 10);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm">
          <Link to="/profesionales"><ArrowLeft className="mr-2 h-4 w-4" /> Volver</Link>
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" /> Editar
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline"><Trash2 className="mr-2 h-4 w-4" /> Eliminar</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar profesional?</AlertDialogTitle>
                <AlertDialogDescription>
                  {visits.length > 0
                    ? "No se puede eliminar este profesional porque tiene citas asignadas."
                    : "Esta acción no se puede deshacer."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction disabled={visits.length > 0} onClick={() => deleteMutation.mutate()}>
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-4 space-y-0">
          <Avatar className="h-14 w-14">
            <AvatarFallback style={{ backgroundColor: prof.color, color: "white" }} className="text-lg">
              {prof.firstName[0]}{prof.lastName[0]}
            </AvatarFallback>
          </Avatar>
          <div>
            <CardTitle>{prof.firstName} {prof.lastName}</CardTitle>
            <p className="text-sm text-muted-foreground">{prof.specialty}</p>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {prof.email}</div>
          <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> {prof.phone}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Próximas citas</CardTitle>
        </CardHeader>
        <CardContent>
          {upcoming.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">Sin citas próximas.</p>
          ) : (
            <ul className="divide-y">
              {upcoming.map((v) => {
                const patient = patientById[v.patientId];
                return (
                  <li key={v.id} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{format(new Date(v.start), "d MMM HH:mm", { locale: es })}</Badge>
                      <Link to="/pacientes/$id" params={{ id: v.patientId }} className="text-sm hover:underline">
                        {patient ? `${patient.firstName} ${patient.lastName}` : "Paciente"}
                      </Link>
                    </div>
                    <span className="text-sm text-muted-foreground">{v.reason}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <ProfessionalDialog open={editOpen} onOpenChange={setEditOpen} professional={prof} />
    </div>
  );
}
